import { type ReactNode, useCallback, useEffect, useMemo } from 'react'

import { useListOrganizationVesselsQuery } from '@/api/queries/useVesselQuery'
import { useUserContext } from '@/providers/user/useUserContext'

import { ConnectedAccountContext } from './ConnectedAccountContext'
import { mapVesselListConnectedAccountsToAwx } from './mapVesselListToConnectedAccounts'

export const ConnectedAccountProvider = ({ children }: { children: ReactNode }) => {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId, updateActiveConnectedAccountId } =
    useUserContext()

  const { data, isLoading, refetch } = useListOrganizationVesselsQuery({
    enabled: Boolean(activeOrganizationId),
  })

  const selectedVesselEntry = useMemo(() => {
    if (!data?.vessels?.length || !activeVesselId) return null
    return data.vessels.find((v) => v.vessel.id === activeVesselId) ?? null
  }, [data?.vessels, activeVesselId])

  const connectedAccounts = useMemo(() => {
    if (!selectedVesselEntry || !activeOrganizationId) return []
    return mapVesselListConnectedAccountsToAwx(
      selectedVesselEntry.vessel.id,
      activeOrganizationId,
      selectedVesselEntry.connectedAccounts,
    )
  }, [selectedVesselEntry, activeOrganizationId])

  const activeConnectedAccount = useMemo(() => {
    if (!activeConnectedAccountId || !connectedAccounts.length) return null
    return connectedAccounts.find((a) => a.id === activeConnectedAccountId) ?? null
  }, [connectedAccounts, activeConnectedAccountId])

  const activeModule = activeConnectedAccount?.type ?? null

  const currentVesselName = selectedVesselEntry?.vessel.name ?? null

  // Every vessel the user can access in this org. The backend already role-scopes this list
  // (org-wide for privileged roles, UserVessel-linked for VESSEL_ADMIN), so membership here is a
  // faithful mirror of the backend's vessel-scoped permission gate.
  const accessibleVesselIds = useMemo(() => data?.vessels?.map((v) => v.vessel.id) ?? [], [data?.vessels])

  const refreshConnectedAccounts = useCallback(async () => {
    if (!activeOrganizationId || !activeVesselId) return
    await refetch()
  }, [activeOrganizationId, activeVesselId, refetch])

  useEffect(() => {
    if (!connectedAccounts.length) return
    const storedExists = connectedAccounts.some((a) => a.id === activeConnectedAccountId)
    if ((!activeConnectedAccountId || !storedExists) && connectedAccounts[0]?.id) {
      updateActiveConnectedAccountId(connectedAccounts[0].id)
    }
  }, [connectedAccounts, activeConnectedAccountId, updateActiveConnectedAccountId])

  const value = useMemo(
    () => ({
      connectedAccounts,
      activeConnectedAccount,
      activeModule,
      currentVesselName,
      accessibleVesselIds,
      refreshConnectedAccounts,
      isLoading,
    }),
    [
      connectedAccounts,
      activeConnectedAccount,
      activeModule,
      currentVesselName,
      accessibleVesselIds,
      refreshConnectedAccounts,
      isLoading,
    ],
  )

  return <ConnectedAccountContext.Provider value={value}>{children}</ConnectedAccountContext.Provider>
}
